// © Microsoft Corporation. All rights reserved.

import { FluentSelect } from "@fluentui/web-components";
import { FASTElement, observable } from "@microsoft/fast-element";
import { SelectPosition } from "@microsoft/fast-foundation";
import { MarketSelectorOptionProps } from "./Interfaces";

export class MarketSelector extends FASTElement {
    /** ID for market selector */
    @observable id: string;

    /** On change callback */
    @observable onValueChange: (market: string) => void;

    /** Default selected market id */
    @observable defaultMarketId: string;

    /** Selected option id */
    @observable selectedMarketId: string;

    /** List of markets */
    @observable marketList: MarketSelectorOptionProps[] = [];

    /** Flag to disable market selector */
    @observable disabled = false;

    /** Dropdown menu select position */
    @observable selectPosition: SelectPosition = SelectPosition.below;

    /** Label for market selector */
    @observable label: string;

    /** Telemetry tag */
    @observable marketSelectorMetaTag: string;

    /** Index of selected market */
    @observable selectedIndex: number;

    /** Ref for select web component */
    public select: FluentSelect;

    /**
     * Callback for when element is inserted into the DOM
     */
    public connectedCallback(): void {
        super.connectedCallback();

        /**
         * TODO: This is a temporary workaround. Currently, there is a bug in the select web component
         * that causes the dropdown to not scroll to the selected value (https://github.com/microsoft/fast/issues/4581),
         * so will use this override until bug is fixed.
         */
        if (this.select) {
            this.select.focusAndScrollOptionIntoView = () => {
                window.requestAnimationFrame(() => {
                    if (this.select.firstSelectedOption) {
                        this.select.firstSelectedOption.focus();
                        this.select.firstSelectedOption.scrollIntoView({ block: "center" });
                    }
                });
            };
        }
    }

    /**
     * Handler for select change events. Will call onValueChange callback with new selected market.
     * @param e change event
     */
    public onChange = (e: CustomEvent) => {
        const newMarket = this.select && this.select.value;
        if (newMarket && this.onValueChange) {
            this.onValueChange(newMarket);
        }

        this.selectedMarketId = newMarket;
        this.$emit("change", { market: newMarket });
    };

    /**
     * When default market id is updated, set the appropriate selected market
     * @param oldValue previous default market id
     * @param newValue new default market id
     * @returns void
     */
    private defaultMarketIdChanged(oldValue: string, newValue: string): void {
        if (oldValue === newValue || !newValue) {
            return;
        }

        if (this.selectedMarketId) {
            this.setSelectedMarket(this.selectedMarketId);
            return;
        }

        this.setSelectedMarket(newValue);
    }

    /**
     * When selected market id is updated, set the selected market
     * @param oldValue previous selected market id
     * @param newValue new selected market id
     * @returns void
     */
    private selectedMarketIdChanged(oldValue: string, newValue: string): void {
        if (oldValue === newValue || !newValue) {
            return;
        }

        this.setSelectedMarket(newValue);
    }

    /**
     * When the market list is updated, check if the new value is valid and set the selected market
     * @param oldValue previous market list
     * @param newValue new market list
     * @returns
     */
    private marketListChanged(oldValue: MarketSelectorOptionProps[], newValue: MarketSelectorOptionProps[]): void {
        if (!newValue || !newValue.length) {
            return;
        }

        this.setSelectedMarket(this.selectedMarketId || this.defaultMarketId);
    }

    /**
     * Updates the selected index for the select component. Selected index corresponds with the selected market
     * @param marketId market id
     * @returns void
     */
    private setSelectedMarket(marketId: string): void {
        if (!marketId) {
            return;
        }

        const selectedMarketIndex = this.marketList && this.marketList.findIndex(market => market.id === marketId);
        if (selectedMarketIndex < 0) {
            return;
        }

        window.requestAnimationFrame(() => {
            this.selectedIndex = selectedMarketIndex;
            // window.requestAnimationFrame(() => this.select && this.select.focusAndScrollOptionIntoView());
        });
    }
}